
import { CallbacksManager } from "../../utils/CallbacksManager";
import { PublicColorKey } from "../editor/ColorPalette";
import { BackendService } from "./Backend";


export type AbstractResponseJson = Record<string, unknown>;


export enum SessionState {

    /** Údaje nejsou vůbec vytvářeny */
    NEW = 0,

    /** Je zařazeno ve validační frontě */
    ENQUEUED_FOR_VALIDATION = 1,

    /** Údaje jsou právě kontrolovány */
    BEING_CONTROLLED = 2,

    /** Vše je v pořádku, data jsou vyplněna a čekají na stažení do videomappingu */
    SUCCESSFULLY_VALIDATED = 3,

    /** Data byla stažena do videomappingu a čekají na přehrávání */
    DOWNLOADED_TO_ELECTRON = 4,

    /** Data jsou právě přehrávána */
    PLAYED = 5

}

export type ConnectionResponse = AbstractResponseJson & {
    isNewSession: boolean,
    existingSession?: SessionRecordFull
}

export type SubmissionResponse = AbstractResponseJson & {
    success: boolean,
}


export type ArchivedRecord = {
    id: number;
    sessionid: string;
    iteration: number;
    ip: string;
    line: string;
    color_a: string;
    color_b: string;
    started: string;
    finished: string;
    comment?: string;
}


export type SessionRecordFull = {
    /** UUID identifikátor session */
    sessionid: string;
    /** Aktuální stav session */
    state: SessionState;
    /** Pořadí obrázku, který uživatel odesílá */
    iteration: number;
    /** Pořadí pokusu, kdy se uživatel snaží validovat současný obrázek */
    attempt: number;

    /** Počet banů, který tento uživatel dostal za neúspěšné validace */
    banlevel: number;

    // Časy

    /** Kdy byl uživatel poprvé připojen? */
    connected: string;

    /** Kdy bylo zařazeno do fronty k validaci? */
    enqueued_for_validation: string | null;

    /** Kdy začala validace? */
    validation_started_at?: string | null;

    /** Kdy byla sessiona úspěšně validována? */
    submitted: string | null;

    /** Kdy začalo přehrávání session? */
    started: string | null;


    // Vzhled a data

    line: string | null;
    color_a: PublicColorKey | null;
    color_b: PublicColorKey | null;
    comment: string | null;
    image_data?: string | null;


};

export class Api {

    private _loading: boolean = false;
    public get loading() { return this._loading; }
    public onLoadingChanged: CallbacksManager<(loading: boolean) => void> = new CallbacksManager();

    private _session?: SessionRecordFull;
    public get session() { return this._session; }
    public onSessionChanged: CallbacksManager<(session?: SessionRecordFull) => void> = new CallbacksManager();

    private _errorMsg?: string;
    public get errorMsg() { return this._errorMsg; }
    public onErrorMsgChanged: CallbacksManager<(errorMsg?: string) => void> = new CallbacksManager();

    private _hasBeenHereBefore: boolean = false;
    public get hasBeenHereBefore() { return this._hasBeenHereBefore; }

    private lastApiCallMs: number = Date.now();
    public get timeSinceLastApiCallMs() {
        return Date.now() - this.lastApiCallMs;
    }

    public static minApiCallIntervalMs: number = 500;

    /** Ochrana proti příliš častým api callům */
    private mayDoApiCall(): boolean {
        return this.timeSinceLastApiCallMs >= Api.minApiCallIntervalMs;
    }

    /** URL adresa, na kterou budou směřovány všechny požadavky */
    private readonly apiUrl: string;

    constructor(
        /** Odkaz na backend */
        private readonly backend: BackendService,
        /** Základní URL adresa backendového serveru */
        private readonly serverUrl: string,
        /** Základní segment URL, za nímž se nachází API */
        private readonly apiBase: string,
        /** Segment URL, za nímž se nachází ta část API, která je určena pro klienta */
        private readonly clientEndpoint: string
    ) {

        this.apiUrl = [
            this.serverUrl,
            this.apiBase,
            this.clientEndpoint
        ].join('/');

    }

    public async connect(): Promise<ConnectionResponse | undefined> {


        if (this.mayDoApiCall() === false) {
            // throw new Error("Api: Příliš časté volání API.");
        }

        this.startLoading();

        const result = await fetch(this.apiUrl + "/connect", {
            credentials: "include"
        });

        if (result.ok) {

            const json = await result.json() as ConnectionResponse;

            console.log(json);
            this.endLoading();
            this._session = json.existingSession;
            this.onSessionChanged.call(this._session);
            this._hasBeenHereBefore = json.isNewSession === false;
            return json;

        }

    }


    public async submit(
        line: string,
        colorA: string,
        colorB: string,
        imageData: string,
        comment: string
    ): Promise<boolean> {

        this.startLoading();

        const payload = {
            line,
            colorA,
            colorB,
            imageData,
            comment
        };

        const maxRetries = 3;
        const timeoutMs = 20_000;
        let lastError: string = "Unknown error";

        for (let attempt = 0; attempt < maxRetries; attempt++) {

            if (attempt > 0) {

                const delayMs = Math.min(4000, 1000 * Math.pow(2, attempt - 1));
                console.warn(`Retrying submit (attempt ${attempt + 1}/${maxRetries}) after error: ${lastError}`);
                await new Promise(resolve => setTimeout(resolve, delayMs));

            }

            try {

                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

                let result: Response;

                try {

                    result = await fetch(
                        this.apiUrl + '/submit',
                        {
                            method: 'POST',
                            body: JSON.stringify(payload),
                            credentials: "include",
                            headers: {
                                "Content-Type": "application/json",
                                "Accept": "application/json"
                            },
                            signal: controller.signal,
                            keepalive: true
                        }
                    );

                } finally {

                    clearTimeout(timeoutId);

                }

                if (result.ok) {
                    this.endLoading();
                    return result.status === 200;
                }

                // Server odpověděl chybou – přečti detail
                let serverMessage = `Server error ${result.status}`;
                try {
                    const errorJson = await result.json();
                    if (errorJson?.message) {
                        serverMessage = errorJson.message;
                    }
                } catch {
                    // Nelze parsovat JSON – použij status text
                    serverMessage = result.statusText || serverMessage;
                }

                // 4xx chyby (klientská chyba) – nemá smysl retryovat
                if (result.status >= 400 && result.status < 500) {
                    this.endLoading(serverMessage);
                    return false;
                }

                // 5xx – zkusíme retry
                lastError = serverMessage;


            } catch (error) {

                // Fetch selhal – síťová chyba, timeout, abort
                if (error instanceof DOMException && error.name === "AbortError") {
                    lastError = "Request timed out";
                } else if (error instanceof TypeError) {
                    // TypeError = typicky síťová chyba (offline, DNS, CORS...)
                    lastError = "Network error – check your connection";
                } else {
                    lastError = error instanceof Error ? error.message : "Unknown error";
                }

                console.error(`Submit attempt ${attempt + 1} failed:`, lastError);

            }

        }



        this.endLoading(lastError);

        return false;

    }


    private startLoading() {
        this._loading = true;
        this.onLoadingChanged.call(this._loading);
        this.lastApiCallMs = Date.now();
        if (this._errorMsg !== undefined) {
            this._errorMsg = undefined;
            this.onErrorMsgChanged.call(this._errorMsg);
        }
    }

    private endLoading(
        errorMsg?: string
    ) {
        this._loading = false;
        this.onLoadingChanged.call(this._loading);
        this.lastApiCallMs = Date.now();
        if (this._errorMsg !== errorMsg) {
            this._errorMsg = errorMsg;
            this.onErrorMsgChanged.call(this._errorMsg);
        }
    }

}